import {
  YES_NO,
  applicantSection,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E6. Wniosek o wydanie zezwolenia (identyfikatora) na wjazd w strefę ograniczonego ruchu — art. 10 Prawa o ruchu
 * drogowym (zarządzanie ruchem), znak B-1 z tabliczką dla posiadaczy zezwoleń; zasady strefy określa zarządzenie lub
 * regulamin gminy. Zezwolenie nie jest decyzją administracyjną. Brak wzoru ogólnopolskiego; układ jak wniosek ZDMK-70
 * Zarządu Dróg Miasta Krakowa (https://kontakt.krakow.pl/c/document_library/get_file?uuid=3063ac57-50fe-dfdb-0e0c-b1f4715dc320&groupId=536063
 * — pobrany 2026-10-22): wnioskodawca, uzasadnienie, strefa, zezwolenie krótko- lub długoterminowe, termin wjazdu,
 * marka/model/numer rejestracyjny (VIN) i DMC pojazdów, załączniki zależne od rodzaju wjazdu. Kategorie uprawnionych
 * i oświadczenie dla pojazdów powyżej 3,5 t — według katalogu (GZD Gdańsk); lista do potwierdzenia z zarządcą drogi.
 */
const RESIDENT_OR_BUSINESS: EbiCondition = { field: "kategoria", in: ["mieszkaniec", "przedsiebiorca"] };
const WORKS_OR_DELIVERY: EbiCondition = { field: "kategoria", in: ["roboty", "dostawy"] };
const HEAVY: EbiCondition = { field: "ponad35t", equals: "tak" };

const E6: IEbiFormDefinition = {
  code: "E6",
  version: 1,
  title: "Wniosek o wydanie zezwolenia na wjazd w strefę ograniczonego ruchu",
  category: EEbiFormCategory.Roads,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Zezwolenie (identyfikator) uprawniające do wjazdu pojazdem w strefę lub obszar objęty ograniczeniami w ruchu.",
  keywords: ["wjazd", "strefa ograniczonego ruchu", "zezwolenie", "identyfikator", "znak B-1", "stare miasto", "dostawy"],
  organ: "Zarządca drogi (zarząd dróg) — według organizacji ruchu i zarządzenia wójta / burmistrza / prezydenta miasta",
  legalBasis:
    "art. 10 ustawy z dnia 20 czerwca 1997 r. — Prawo o ruchu drogowym; rozporządzenie Ministra Infrastruktury z dnia 23 września 2003 r. w sprawie szczegółowych warunków zarządzania ruchem na drogach oraz wykonywania nadzoru nad tym zarządzaniem; zarządzenie lub regulamin strefy",
  request: "Wnoszę o wydanie zezwolenia (identyfikatora) uprawniającego do wjazdu w strefę ograniczonego ruchu pojazdami wskazanymi we wniosku.",
  feeLabel: "zwykle bez opłat",
  fee: "Zwykle bez opłaty (np. Kraków, Gdańsk); jeśli regulamin strefy przewiduje opłatę, urząd poda kwotę. Pełnomocnictwo — 17 zł.",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "zezwolenie",
      title: "Zezwolenie",
      fields: [
        {
          code: "strefa",
          type: "text",
          label: "Strefa lub obszar objęty ograniczeniami w ruchu",
          hint: "Nazwę strefy podaje zarządzenie lub regulamin gminy.",
          required: true,
          maxLength: 200,
          placeholder: "np. Stare Miasto",
        },
        {
          code: "kategoria",
          type: "radio",
          label: "Wnoszę o zezwolenie jako",
          required: true,
          options: [
            { value: "mieszkaniec", label: "mieszkaniec strefy" },
            { value: "przedsiebiorca", label: "przedsiębiorca albo właściciel lub najemca lokalu w strefie" },
            { value: "dostawy", label: "dostawca (zaopatrzenie)" },
            { value: "roboty", label: "wykonawca robót budowlanych lub remontowych" },
            { value: "przeprowadzka", label: "osoba przeprowadzająca się" },
            { value: "inne", label: "inne" },
          ],
        },
        {
          code: "kategoriaInna",
          type: "text",
          label: "Podaj, w jakim charakterze",
          required: true,
          visibleWhen: { field: "kategoria", equals: "inne" },
          maxLength: 200,
        },
        {
          code: "rodzajZezwolenia",
          type: "radio",
          label: "Zezwolenie",
          required: true,
          options: [
            { value: "krotkoterminowe", label: "jednorazowe lub krótkoterminowe" },
            { value: "dlugoterminowe", label: "okresowe (długoterminowe)" },
          ],
        },
        { code: "okresOd", type: "date", label: "Termin planowanego wjazdu — od", required: true, width: "half" },
        { code: "okresDo", type: "date", label: "do", required: true, width: "half" },
        { code: "miejsceDocelowe", type: "text", label: "Miejsce docelowe (ulica, numer posesji)", required: true, maxLength: 300 },
        { code: "uzasadnienie", type: "textarea", label: "Uzasadnienie", required: true, maxLength: 3000 },
      ],
    },
    {
      code: "listaPojazdow",
      title: "Pojazdy",
      fields: [
        {
          code: "pojazdy",
          type: "repeat",
          label: "Pojazdy, które mają wjeżdżać do strefy",
          itemLabel: "Pojazd",
          required: true,
          min: 1,
          max: 10,
          fields: [
            { code: "marka", type: "text", label: "Marka i model", required: true, width: "half", maxLength: 100 },
            {
              code: "nrRejestracyjny",
              type: "text",
              label: "Numer rejestracyjny (pojazd niezarejestrowany — VIN)",
              required: true,
              width: "half",
              maxLength: 20,
            },
            { code: "dmc", type: "number", label: "Dopuszczalna masa całkowita [t]", required: true, width: "third", decimals: 2, min: 0 },
          ],
        },
        {
          code: "pojazdNieWlasny",
          type: "radio",
          label: "Czy któryś z pojazdów nie jest Twoją własnością (leasing, najem, użyczenie)?",
          required: true,
          options: YES_NO,
        },
        {
          code: "ponad35t",
          type: "radio",
          label: "Czy któryś z pojazdów ma dopuszczalną masę całkowitą powyżej 3,5 t?",
          required: true,
          options: YES_NO,
        },
        {
          code: "oswiadczenieNawierzchnia",
          type: "declaration",
          label: "Zobowiązuję się pokryć koszty naprawy uszkodzeń nawierzchni drogi spowodowanych przez pojazd o dopuszczalnej masie całkowitej powyżej 3,5 t.",
          required: true,
          visibleWhen: HEAVY,
        },
      ],
    },
    replySection("Sposób odbioru zezwolenia"),
  ],
  attachments: [
    {
      code: "dowodyRejestracyjne",
      label: "Dowody rejestracyjne pojazdów",
      hint: "Większość zarządców dróg o nie prosi; w niektórych gminach okazuje się je przy odbiorze zezwolenia.",
      multiple: true,
    },
    {
      code: "umowaPojazd",
      label: "Umowa leasingu, najmu lub użyczenia pojazdu",
      hint: "Gdy pojazd nie jest Twoją własnością — zarządcy dróg zwykle o nią proszą.",
      visibleWhen: { field: "pojazdNieWlasny", equals: "tak" },
      multiple: true,
    },
    {
      code: "tytulDoLokalu",
      label: "Zaświadczenie o zameldowaniu, wydruk z księgi wieczystej albo inny tytuł prawny do lokalu w strefie",
      visibleWhen: RESIDENT_OR_BUSINESS,
      required: true,
    },
    {
      code: "umowaRoboty",
      label: "Umowa z zamawiającym roboty lub dostawy albo oświadczenie właściciela posesji lub pozwolenie na roboty",
      hint: "Np. umowa z zarządem dróg lub przedsiębiorstwem komunalnym; przy robotach u osób fizycznych — oświadczenie właściciela posesji.",
      visibleWhen: WORKS_OR_DELIVERY,
      required: true,
      multiple: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default E6;
